import { useState, useRef, useEffect } from "react";
import usePageLoader from '../hooks/usePageLoader';
import { ShimmerBox, ShimmerCircle, ShimmerListItem } from '../components/common/Shimmer';
import {
  FiSearch, FiSend, FiPaperclip, FiMoreVertical,
  FiX, FiCheckCircle, FiUser, FiGlobe, FiMonitor,
  FiClock, FiMapPin, FiTag, FiMessageSquare, FiActivity,
} from "react-icons/fi";
import { BsRobot } from "react-icons/bs";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────
   MOCK DATA
───────────────────────────────────────────── */
const MOCK_CONVERSATIONS = [
  {
    id: 1, user: "Arun Kumar", email: "arun@gmail.com",
    avatar: "https://i.pravatar.cc/100?img=1",
    lastMessage: "Can you explain your pricing plans?",
    lastTime: "2m ago", unreadCount: 2, status: "open",
    meta: { visitorId: "VIS-98321", ip: "103.21.244.1", browser: "Chrome 120.0", os: "Windows 11", country: "🇮🇳 India", referrer: "google.com", firstSeen: "May 11, 2026", lastSeen: "2 min ago", pagesViewed: 4 },
    messages: [
      { sender: "user", text: "Hi, I want to know about your pricing plans", time: "10:32 AM" },
      { sender: "bot",  text: "Hi Arun! Welcome to NexaGPT. We offer three plans: Starter at $29/mo, Pro at $79/mo, and Enterprise with custom pricing.", time: "10:32 AM" },
      { sender: "user", text: "What's included in the Pro plan?", time: "10:33 AM" },
      { sender: "bot",  text: "Pro includes 10 chatbots, 100k messages/month, CRM integrations (HubSpot & Salesforce), priority support, and advanced analytics.", time: "10:33 AM" },
      { sender: "user", text: "Can you explain your pricing plans?", time: "10:35 AM" },
    ],
  },
  {
    id: 2, user: "Priya Sharma", email: "priya@techcorp.com",
    avatar: "https://i.pravatar.cc/100?img=5",
    lastMessage: "Thanks, that helps a lot!",
    lastTime: "15m ago", unreadCount: 0, status: "closed",
    meta: { visitorId: "VIS-55442", ip: "45.112.88.21", browser: "Safari 17.0", os: "macOS Sonoma", country: "🇮🇳 India", referrer: "direct", firstSeen: "May 10, 2026", lastSeen: "15 min ago", pagesViewed: 7 },
    messages: [
      { sender: "user", text: "Is NexaGPT suitable for startups?", time: "Yesterday 3:10 PM" },
      { sender: "bot",  text: "Absolutely! Our Starter plan is designed for small teams getting started with AI chatbots — 2 bots, 10k messages, no credit card required.", time: "Yesterday 3:10 PM" },
      { sender: "user", text: "How long does it take to set up?", time: "Yesterday 3:11 PM" },
      { sender: "bot",  text: "You can have your chatbot live in under 5 minutes! Connect your URL and NexaGPT crawls & trains automatically.", time: "Yesterday 3:11 PM" },
      { sender: "user", text: "Thanks, that helps a lot!", time: "Yesterday 3:12 PM" },
    ],
  },
  {
    id: 3, user: "Rahul Verma", email: "rahul@startup.io",
    avatar: "https://i.pravatar.cc/100?img=12",
    lastMessage: "Does it support Notion integration?",
    lastTime: "1h ago", unreadCount: 1, status: "open",
    meta: { visitorId: "VIS-77213", ip: "117.55.22.90", browser: "Chrome 121.0", os: "Windows 11", country: "🇮🇳 India", referrer: "linkedin.com", firstSeen: "May 11, 2026", lastSeen: "1 hour ago", pagesViewed: 3 },
    messages: [
      { sender: "user", text: "Does NexaGPT support Notion integration?", time: "9:15 AM" },
      { sender: "bot",  text: "Yes! NexaGPT has native Notion integration. You can connect your workspace and train on pages, databases, and documents.", time: "9:15 AM" },
      { sender: "user", text: "Does it support Notion integration?", time: "9:18 AM" },
    ],
  },
  {
    id: 4, user: "Meena Iyer", email: "meena@design.co",
    avatar: "https://i.pravatar.cc/100?img=9",
    lastMessage: "Can I embed this on Webflow?",
    lastTime: "3h ago", unreadCount: 0, status: "open",
    meta: { visitorId: "VIS-34891", ip: "152.58.11.204", browser: "Firefox 122.0", os: "macOS Ventura", country: "🇮🇳 India", referrer: "twitter.com", firstSeen: "May 9, 2026", lastSeen: "3 hours ago", pagesViewed: 5 },
    messages: [
      { sender: "user", text: "Can I embed the chatbot on a Webflow site?", time: "7:22 AM" },
      { sender: "bot",  text: "Yes! Copy the embed script from your dashboard and paste it before </body> in Webflow's custom code section. Works instantly.", time: "7:22 AM" },
      { sender: "user", text: "Can I embed this on Webflow?", time: "7:24 AM" },
    ],
  },
  {
    id: 5, user: "Suresh Nair", email: "suresh@enterprise.com",
    avatar: "https://i.pravatar.cc/100?img=15",
    lastMessage: "What is the message limit on Starter?",
    lastTime: "5h ago", unreadCount: 0, status: "closed",
    meta: { visitorId: "VIS-21034", ip: "49.204.185.7", browser: "Edge 121.0", os: "Windows 10", country: "🇮🇳 India", referrer: "google.com", firstSeen: "May 8, 2026", lastSeen: "5 hours ago", pagesViewed: 9 },
    messages: [
      { sender: "user", text: "What is the message limit on the Starter plan?", time: "5:30 AM" },
      { sender: "bot",  text: "Starter plan: 10,000 messages/month across 2 chatbots. Pro: 100,000 messages. Enterprise: unlimited.", time: "5:30 AM" },
      { sender: "user", text: "What is the message limit on Starter?", time: "5:31 AM" },
      { sender: "bot",  text: "10,000 messages/month on Starter. You can upgrade to Pro anytime from your billing page.", time: "5:31 AM" },
    ],
  },
  {
    id: 6, user: "Kavitha Rajan", email: "kavitha@saas.app",
    avatar: "https://i.pravatar.cc/100?img=20",
    lastMessage: "Do you support multi-language?",
    lastTime: "1d ago", unreadCount: 0, status: "closed",
    meta: { visitorId: "VIS-66102", ip: "223.233.77.12", browser: "Chrome 120.0", os: "Android 14", country: "🇸🇬 Singapore", referrer: "producthunt.com", firstSeen: "May 10, 2026", lastSeen: "1 day ago", pagesViewed: 12 },
    messages: [
      { sender: "user", text: "Does NexaGPT support multiple languages?", time: "Yesterday 11:00 AM" },
      { sender: "bot",  text: "Yes! NexaGPT supports 15+ languages including English, Hindi, Spanish, French, German, Arabic, and Japanese.", time: "Yesterday 11:00 AM" },
      { sender: "user", text: "Can it auto-detect the visitor's language?", time: "Yesterday 11:02 AM" },
      { sender: "bot",  text: "Absolutely! Enable auto-detect in Settings → Localization. The chatbot will reply in the visitor's own language.", time: "Yesterday 11:02 AM" },
      { sender: "user", text: "Do you support multi-language?", time: "Yesterday 11:05 AM" },
    ],
  },
];

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
function InboxSkeleton() {
  return (
    <div className="flex-1 overflow-hidden flex">
      <div className="w-72 border-r flex flex-col bg-white shrink-0">
        <div className="px-3 py-3 border-b">
          <ShimmerBox h="h-9" w="w-full" rounded="rounded-lg" />
        </div>
        <div className="flex border-b px-1 pt-1 gap-1">
          {[...Array(3)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-t-lg" />)}
        </div>
        <div className="flex-1 overflow-y-auto">
          {[...Array(6)].map((_, i) => <ShimmerListItem key={i} />)}
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col">
          <div className="px-5 py-3 border-b flex items-center gap-3">
            <ShimmerCircle size="w-9 h-9" />
            <div className="flex-1 space-y-1.5">
              <ShimmerBox h="h-3" w="w-32" />
              <ShimmerBox h="h-2.5" w="w-24" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-gray-50">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={`flex gap-3 ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}>
                <ShimmerCircle size="w-7 h-7" />
                <ShimmerBox h="h-12" w="w-48" rounded="rounded-2xl" />
              </div>
            ))}
          </div>
          <div className="border-t px-4 py-3">
            <ShimmerBox h="h-10" w="w-full" rounded="rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InboxPage() {
  const [conversations, setConversations] = useState(MOCK_CONVERSATIONS);
  const [selectedId, setSelectedId]       = useState(MOCK_CONVERSATIONS[0].id);
  const loading = usePageLoader();
  const [showProfile, setShowProfile]     = useState(false);
  const [filter, setFilter]               = useState("all");
  const [search, setSearch]               = useState("");

  const selectedChat = conversations.find((c) => c.id === selectedId);

  const openCount   = conversations.filter((c) => c.status === "open").length;
  const closedCount = conversations.filter((c) => c.status === "closed").length;

  const visible = conversations.filter((c) => {
    const matchesFilter = filter === "all" || c.status === filter;
    const matchesSearch = c.user.toLowerCase().includes(search.toLowerCase()) ||
                          c.lastMessage.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleReply = (text) => {
    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, messages: [...c.messages, { sender: "agent", text, time: now }], lastMessage: text, lastTime: "just now", unreadCount: 0 }
          : c
      )
    );
  };

  const handleClose = () => {
    setConversations((prev) =>
      prev.map((c) => c.id === selectedId ? { ...c, status: "closed" } : c)
    );
    toast.success("Conversation closed.");
  };

  const handleReopen = () => {
    setConversations((prev) =>
      prev.map((c) => c.id === selectedId ? { ...c, status: "open" } : c)
    );
    toast.success("Conversation reopened.");
  };

  const selectChat = (id) => {
    setSelectedId(id);
    setShowProfile(false);
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c))
    );
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Page Header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Inbox</h1>
          <p className="text-sm text-gray-500 mt-0.5">All chatbot conversations</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{openCount} open
          </span>
          <span className="flex items-center gap-1.5 bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs font-medium">
            {closedCount} closed
          </span>
        </div>
      </div>

      {loading ? <InboxSkeleton /> : <div className="flex-1 overflow-hidden flex">

        {/* ── Sidebar ── */}
        <div className="w-72 border-r flex flex-col bg-white shrink-0">

          {/* Search */}
          <div className="px-3 py-3 border-b">
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
              <FiSearch size={14} className="text-gray-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations…"
                className="bg-transparent text-sm flex-1 outline-none text-gray-700 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium">
            {[
              { key: "all",    label: `All (${conversations.length})` },
              { key: "open",   label: `Open (${openCount})` },
              { key: "closed", label: `Closed (${closedCount})` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`flex-1 py-1.5 px-3 transition ${
                  filter === t.key ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {visible.length === 0 && (
              <p className="text-center text-sm text-gray-400 mt-10">No conversations found.</p>
            )}
            {visible.map((chat) => (
              <button
                key={chat.id}
                onClick={() => selectChat(chat.id)}
                className={`w-full px-4 py-3 border-b flex gap-3 text-left transition ${
                  selectedId === chat.id
                    ? "bg-primary/5 border-l-2 border-l-primary"
                    : "hover:bg-gray-50 border-l-2 border-l-transparent"
                }`}
              >
                <div className="relative shrink-0">
                  <img src={chat.avatar} alt="" className="w-9 h-9 rounded-full" />
                  <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                    chat.status === "open" ? "bg-green-500" : "bg-gray-300"
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate">{chat.user}</p>
                    <span className="text-[10px] text-gray-400 shrink-0 ml-1">{chat.lastTime}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-gray-500 truncate">{chat.lastMessage}</p>
                    {chat.unreadCount > 0 && (
                      <span className="ml-1 shrink-0 text-[10px] bg-primary text-white w-4 h-4 rounded-full flex items-center justify-center font-bold">
                        {chat.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Chat + Profile ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* Chat Window */}
          <div className={`flex flex-col transition-all duration-200 ${showProfile ? "flex-1" : "flex-1"}`}>
            {selectedChat
              ? <ChatWindow
                  chat={selectedChat}
                  showProfile={showProfile}
                  onToggleProfile={() => setShowProfile((v) => !v)}
                  onReply={handleReply}
                  onClose={handleClose}
                  onReopen={handleReopen}
                />
              : <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Select a conversation
                </div>
            }
          </div>

          {/* Profile Panel */}
          {showProfile && selectedChat && (
            <ProfilePanel chat={selectedChat} onClose={() => setShowProfile(false)} />
          )}
        </div>

      </div>}
    </div>
  );
}

/* ─────────────────────────────────────────────
   CHAT WINDOW
───────────────────────────────────────────── */
function ChatWindow({ chat, showProfile, onToggleProfile, onReply, onClose, onReopen }) {
  const [reply, setReply]     = useState("");
  const bottomRef             = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat?.messages]);

  const handleSend = () => {
    const text = reply.trim();
    if (!text) return;
    onReply(text);
    setReply("");
  };

  return (
    <div className="flex flex-col h-full">

      {/* Chat Header */}
      <div className="shrink-0 px-5 py-3 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={chat.avatar} alt="" className="w-9 h-9 rounded-full" />
            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
              chat.status === "open" ? "bg-green-500" : "bg-gray-300"
            }`} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{chat.user}</p>
            <p className="text-xs text-gray-500">{chat.email}</p>
          </div>
          <span className={`ml-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
            chat.status === "open"
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}>
            {chat.status === "open" ? "Open" : "Closed"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {chat.status === "open" ? (
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-gray-600 transition"
            >
              <FiCheckCircle size={13} /> Close
            </button>
          ) : (
            <button
              onClick={onReopen}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-gray-600 transition"
            >
              Reopen
            </button>
          )}
          <button
            onClick={onToggleProfile}
            className={`p-2 rounded-lg border transition ${showProfile ? "bg-primary/10 border-primary/30 text-primary" : "hover:bg-gray-50 text-gray-500"}`}
            title="Visitor details"
          >
            <FiUser size={14} />
          </button>
          <button className="p-2 rounded-lg border hover:bg-gray-50 text-gray-500 transition">
            <FiMoreVertical size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-gray-50">
        {chat.messages.map((msg, idx) => (
          <MessageBubble key={idx} message={msg} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 border-t bg-white px-4 py-3">
        {chat.status === "closed" && (
          <p className="text-xs text-center text-gray-400 mb-2">
            This conversation is closed.{" "}
            <button onClick={onReopen} className="text-primary hover:underline">Reopen</button> to reply.
          </p>
        )}
        <div className={`flex items-center gap-2 ${chat.status === "closed" ? "opacity-40 pointer-events-none" : ""}`}>
          <button className="p-2 text-gray-400 hover:text-gray-600 transition">
            <FiPaperclip size={16} />
          </button>
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder="Reply as agent…"
            className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            onClick={handleSend}
            disabled={!reply.trim()}
            className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40 transition shrink-0"
          >
            <FiSend size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MESSAGE BUBBLE
───────────────────────────────────────────── */
function MessageBubble({ message }) {
  const isUser  = message.sender === "user";
  const isBot   = message.sender === "bot";
  const isAgent = message.sender === "agent";

  if (isAgent) {
    return (
      <div className="flex justify-end gap-2">
        <div className="max-w-[70%]">
          <p className="text-[10px] text-gray-400 text-right mb-1 font-medium">You (agent)</p>
          <div className="bg-primary text-white px-4 py-2.5 rounded-2xl rounded-tr-sm text-sm leading-relaxed">
            {message.text}
          </div>
          <p className="text-[10px] text-gray-400 text-right mt-1">{message.time}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start gap-2">
      {isBot ? (
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
          <BsRobot size={13} className="text-primary" />
        </div>
      ) : (
        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-1 text-xs font-bold text-gray-600">
          {message.sender === "user" ? "U" : "?"}
        </div>
      )}
      <div className="max-w-[70%]">
        <p className="text-[10px] text-gray-400 mb-1 font-medium">
          {isBot ? "NexaGPT Bot" : "Visitor"}
        </p>
        <div className={`px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm leading-relaxed ${
          isBot ? "bg-white border border-gray-200 text-gray-800 shadow-sm" : "bg-gray-200 text-gray-800"
        }`}>
          {message.text}
        </div>
        <p className="text-[10px] text-gray-400 mt-1">{message.time}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PROFILE PANEL
───────────────────────────────────────────── */
function ProfilePanel({ chat, onClose }) {
  const [tag, setTag]   = useState("");
  const [tags, setTags] = useState(["lead", "pricing"]);
  const [note, setNote] = useState("");

  const addTag = () => {
    const t = tag.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTag("");
  };

  return (
    <div className="w-72 border-l bg-white flex flex-col shrink-0 overflow-hidden">

      {/* Panel header */}
      <div className="shrink-0 px-4 py-3 border-b flex items-center justify-between">
        <p className="font-semibold text-gray-900 text-sm">Visitor Details</p>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition">
          <FiX size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">

        {/* Avatar + Name */}
        <div className="px-4 py-5 text-center border-b">
          <img src={chat.avatar} alt="" className="w-14 h-14 rounded-full mx-auto mb-2" />
          <p className="font-semibold text-gray-900">{chat.user}</p>
          <p className="text-xs text-gray-500 mt-0.5">{chat.email}</p>
          <span className={`inline-block mt-2 text-xs px-2.5 py-0.5 rounded-full font-medium ${
            chat.status === "open" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
          }`}>
            {chat.status === "open" ? "Open" : "Closed"}
          </span>
        </div>

        {/* Activity stats */}
        <div className="grid grid-cols-2 gap-px bg-gray-100 border-b">
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-lg font-bold text-gray-900">{chat.messages.length}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Messages</p>
          </div>
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-lg font-bold text-gray-900">{chat.meta.pagesViewed}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Pages Viewed</p>
          </div>
        </div>

        {/* Visitor info */}
        <div className="px-4 py-4 space-y-3 border-b">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Session Info</p>
          <InfoRow icon={FiMapPin}   label="Location"    value={chat.meta.country} />
          <InfoRow icon={FiGlobe}    label="IP Address"  value={chat.meta.ip} />
          <InfoRow icon={FiMonitor}  label="Browser"     value={`${chat.meta.browser} · ${chat.meta.os}`} />
          <InfoRow icon={FiActivity} label="Referrer"    value={chat.meta.referrer} />
          <InfoRow icon={FiUser}     label="Visitor ID"  value={chat.meta.visitorId} />
        </div>

        {/* Timeline */}
        <div className="px-4 py-4 space-y-3 border-b">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Timeline</p>
          <InfoRow icon={FiClock}       label="First Seen" value={chat.meta.firstSeen} />
          <InfoRow icon={FiMessageSquare} label="Last Seen" value={chat.meta.lastSeen} />
        </div>

        {/* Tags */}
        <div className="px-4 py-4 space-y-3 border-b">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {t}
                <button onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="hover:text-red-500">
                  <FiX size={10} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTag()}
              placeholder="Add tag…"
              className="flex-1 text-xs border rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button onClick={addTag} className="px-2.5 py-1.5 bg-primary text-white text-xs rounded-lg hover:opacity-90">
              <FiTag size={12} />
            </button>
          </div>
        </div>

        {/* Notes */}
        <div className="px-4 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Notes</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Add a private note about this visitor…"
            className="w-full text-xs border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-primary text-gray-700"
          />
          <button
            onClick={() => { if (note.trim()) { toast.success("Note saved."); } }}
            className="mt-2 w-full text-xs py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            Save Note
          </button>
        </div>

      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon size={13} className="text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-gray-400 font-medium">{label}</p>
        <p className="text-xs text-gray-700 truncate">{value}</p>
      </div>
    </div>
  );
}
